"use client";

import type { HeroesBlock, ICmsHeroSplitQuote } from "@mw/shared/payload-types";
import type { ReactNode } from "react";
import type { ILink } from "@ui/components/core/Link";
import type { HeroSplitFeaturesListItem } from "./HeroSplitFeaturesList";

import { EventName } from "@mw/shared/analyticsEventNames";
import { BlockBreadcrumbsV2 } from "@/app/_blocks/BlockBreadcrumbsv2";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { SolidBorder } from "@ui/components/core/SolidBorder";
import { HeroSplitMedia, type SplitMedia } from "./HeroSplitMedia";
import { HeroSplitQuote } from "./HeroSplitQuote";
import { HeroSplitFeaturesList } from "./HeroSplitFeaturesList";

type SplitConfig = NonNullable<HeroesBlock["heroConfiguration"]["split"]>;

export type HeroSplitDecorationItem =
  | { template: "quote"; quote: ICmsHeroSplitQuote }
  | { template: "featuresList"; items: HeroSplitFeaturesListItem[] };

export interface HeroSplitProps {
  title?: string | null;
  description?: string | null;
  breadcrumbs?: { title: string; href?: string }[];
  primaryLink?: ILink | null;
  secondaryLink?: ILink | null;
  media: SplitConfig["media"];
  watchButtonIcon?: ReactNode;
  decorations?: HeroSplitDecorationItem[] | null;
  borderStyle?: SplitConfig["borderStyle"];
}

export function HeroSplit({
  title,
  description,
  breadcrumbs,
  primaryLink,
  secondaryLink,
  media,
  watchButtonIcon,
  decorations,
  borderStyle,
}: HeroSplitProps) {
  const hasBorder = borderStyle !== "none";
  return (
    <div className='tw-relative tw-space-y-6 lg:tw-grid tw-grid-cols-2 lg:tw-space-y-0'>
      {hasBorder && <SolidBorder side='vertical' thickness='w-px' className='tw-hidden lg:tw-block' />}
      {/* Left side — content */}
      <div className='tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-gap-5 tw-py-12 tw-px-4 md:tw-px-12'>
        <div className='tw-flex tw-flex-col tw-gap-5'>
          {breadcrumbs && <BlockBreadcrumbsV2 links={breadcrumbs} size='small' hidenOnMobileAfter={0} />}
          <h1 className='tw-max-w-3xl tw-type-5xl'>{title}</h1>
          <div className='tw-max-w-lg'>
            <p className='tw-type-base tw-text-gray-500'>{description}</p>
            {(primaryLink || secondaryLink) && (
              <div className='tw-flex tw-flex-wrap tw-gap-3 tw-pt-8'>
                {primaryLink && (
                  <LinkTracked
                    {...primaryLink}
                    asButton
                    buttonVariants={{ variant: "primary", size: "lg" }}
                    eventName={EventName.mw_hero_primary_link}
                  />
                )}
                {secondaryLink && (
                  <LinkTracked
                    {...secondaryLink}
                    asButton
                    buttonVariants={{ variant: "elevated", size: "lg" }}
                    eventName={EventName.mw_hero_secondary_link}
                  />
                )}
              </div>
            )}
          </div>
        </div>
        {decorations && decorations.length > 0 && (
          <div className='tw-flex tw-flex-col tw-gap-6'>
            {decorations.map((item, i) => (
              <HeroSplitDecorationRenderer key={i} decoration={item} />
            ))}
          </div>
        )}
      </div>

      {/* Right side — 1:1 media */}
      <HeroSplitMedia media={media as SplitMedia} hasBorder={hasBorder} watchButtonIcon={watchButtonIcon} />
    </div>
  );
}

function HeroSplitDecorationRenderer({ decoration }: { decoration: HeroSplitDecorationItem }) {
  switch (decoration.template) {
    case "quote":
      return <HeroSplitQuote quote={decoration.quote} />;
    case "featuresList":
      return <HeroSplitFeaturesList items={decoration.items} />;
  }
}
